/**
 * Account page — `/account`.
 *
 * Layout (top → bottom):
 *   1. Notifications  — collapsible (max 2 visible by default), with
 *                       "Show all" / "Show less" and "Mark all as read".
 *                       Non-actionable items auto-mark-as-read after a
 *                       short dwell on the page.
 *   2. Profile        — username + email (both read-only), a Log out button,
 *                       and a [More ▾] menu carrying:
 *                         • Change email
 *                         • Change password
 *                         • ──────────
 *                         • Delete my account (red)
 *   3. Account actions — settings / secondary pages.
 *
 * Each destructive / sensitive action lives behind its own modal so the
 * page itself is read-only at a glance and there is no surprise "Save".
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import {
  useDeleteAllNotifications,
  useMarkNotificationRead,
  useNotifications,
  useRespondNotification,
} from "../../api/notifications";
import { useAuth } from "../../auth/AuthProvider";
import { useCars } from "../../api/cars";
import Icon from "../../components/Icon";
import type { NotificationView } from "../../api/types";

import ChangeEmailModal from "./ChangeEmailModal";
import ChangePasswordModal from "./ChangePasswordModal";
import DeleteAccountModal from "./DeleteAccountModal";
import MoreMenu, { MoreMenuDivider, MoreMenuItem } from "./MoreMenu";

// Delay before non-actionable notifications are auto-marked as read.
// Long enough that a user landing on the page to change a setting won't
// accidentally consume them; short enough to feel natural if they do
// linger on the notifications list.
const AUTO_READ_DELAY_MS = 3000;

// How many notifications are shown when the section is collapsed.
const COLLAPSED_COUNT = 2;

export default function AccountPage() {
  const { me } = useAuth();

  if (!me) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  return (
    <>
      <section className="page-section">
        <header className="page-header">
          <h1 className="section-header__title">Account</h1>
        </header>
      </section>

      <NotificationsSection />
      <ProfileSection />
      <AccountActionsSection />
    </>
  );
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

function NotificationsSection() {
  const notifs = useNotifications();
  const respond = useRespondNotification();
  const markRead = useMarkNotificationRead();
  const deleteAll = useDeleteAllNotifications();
  const cars = useCars();

  const list = notifs.data?.notifications ?? [];
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteConfirmRef = useRef<HTMLDivElement | null>(null);

  // Sort: actionable + pending first, then unread informational, then read.
  // Within each bucket newest comes first.
  const sorted = useMemo(() => {
    const score = (n: NotificationView) => {
      if (n.actionable && n.status === "pending") return 0;
      if (n.status === "pending") return 1;
      return 2;
    };
    return [...list].sort((a, b) => {
      const diff = score(a) - score(b);
      if (diff !== 0) return diff;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [list]);

  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_COUNT);
  const hiddenCount = Math.max(sorted.length - COLLAPSED_COUNT, 0);

  // Track which non-actionable pending IDs we've already enqueued for
  // auto-read so we don't fire the mutation twice.
  const [autoReadIds, setAutoReadIds] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const candidates = sorted
      .filter((n) => n.status === "pending" && !n.actionable)
      .map((n) => n.id)
      .filter((id) => !autoReadIds.has(id));
    if (candidates.length === 0) return;

    const timer = window.setTimeout(() => {
      setAutoReadIds((prev) => {
        const next = new Set(prev);
        for (const id of candidates) next.add(id);
        return next;
      });
      for (const id of candidates) {
        markRead.mutate(id);
      }
    }, AUTO_READ_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [autoReadIds, markRead, sorted]);

  const unreadInfoIds = sorted
    .filter((n) => n.status === "pending" && !n.actionable)
    .map((n) => n.id);
  const canMarkAllRead = unreadInfoIds.length > 0;

  useEffect(() => {
    if (!confirmDeleteOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (!deleteConfirmRef.current?.contains(event.target as Node)) {
        setConfirmDeleteOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setConfirmDeleteOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [confirmDeleteOpen]);

  function handleMarkAllRead() {
    setAutoReadIds((prev) => {
      const next = new Set(prev);
      for (const id of unreadInfoIds) next.add(id);
      return next;
    });
    for (const id of unreadInfoIds) {
      markRead.mutate(id);
    }
  }

  async function handleDeleteAll() {
    setDeleteError(null);
    try {
      await deleteAll.mutateAsync();
      setAutoReadIds(new Set());
      setConfirmDeleteOpen(false);
      setExpanded(false);
    } catch (err) {
      setDeleteError(
        err instanceof ApiError ? err.message : "Could not delete notifications.",
      );
    }
  }

  if (notifs.isLoading) {
    return (
      <section className="page-section">
        <h2 className="section-title section-title--with-icon">
          <Icon name="bell" className="section-title__icon" />
          <span>Notifications</span>
        </h2>
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  const carNameById = new Map<number, string>();
  for (const c of cars.data?.cars ?? []) {
    carNameById.set(c.id, c.name);
  }

  return (
    <section className="page-section">
      <header className="notif-section__header">
        <h2 className="section-title section-title--with-icon">
          <Icon name="bell" className="section-title__icon" />
          <span>Notifications</span>
        </h2>
        <div className="notif-section__actions">
          {expanded && canMarkAllRead ? (
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={handleMarkAllRead}
              disabled={markRead.isPending || deleteAll.isPending}
            >
              Mark all as read
            </button>
          ) : null}
          {sorted.length > 0 ? (
            <div className="notif-delete" ref={deleteConfirmRef}>
              <button
                type="button"
                className="button button--ghost button--small"
                onClick={() => {
                  setDeleteError(null);
                  setConfirmDeleteOpen((open) => !open);
                }}
                disabled={deleteAll.isPending}
                aria-expanded={confirmDeleteOpen}
              >
                Delete all
              </button>
              {confirmDeleteOpen ? (
                <div className="notif-delete__confirm" role="dialog" aria-modal="false">
                  <p>Delete all notifications?</p>
                  <div className="notif-delete__actions">
                    <button
                      type="button"
                      className="button button--ghost button--small"
                      onClick={() => setConfirmDeleteOpen(false)}
                      disabled={deleteAll.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="button button--danger button--small"
                      onClick={() => void handleDeleteAll()}
                      disabled={deleteAll.isPending}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>
      {deleteError ? <p className="form-error">{deleteError}</p> : null}
      {sorted.length === 0 ? (
        <p className="section-help">You're all caught up.</p>
      ) : (
        <>
          <ul className="notif-list">
            {visible.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                carName={
                  typeof n.payload.car_name === "string"
                    ? n.payload.car_name
                    : typeof n.payload.car_id === "number"
                      ? carNameById.get(n.payload.car_id)
                      : undefined
                }
                onRespond={(action) =>
                  respond.mutate({ id: n.id, action })
                }
                busy={respond.isPending || markRead.isPending || deleteAll.isPending}
              />
            ))}
          </ul>
          {hiddenCount > 0 ? (
            <button
              type="button"
              className="notif-list__toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? "Show less" : `Show all (${sorted.length})`}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}

interface NotificationRowProps {
  notification: NotificationView;
  carName: string | undefined;
  onRespond: (action: "accept" | "decline") => void;
  busy: boolean;
}

function NotificationRow({
  notification,
  carName,
  onRespond,
  busy,
}: NotificationRowProps) {
  const { type, status, actionable } = notification;
  const carLabel = carName ?? `car #${notification.payload.car_id ?? "?"}`;
  const statusLabel = status !== "pending" && status !== "read"
    ? capitalize(status)
    : null;

  let title = "Notification";
  let body: string | null = null;
  switch (type) {
    case "car_share_invite":
      title = "Share invite";
      body = `You've been invited to ${carLabel} as ${
        notification.payload.role ?? "viewer"
      }.`;
      break;
    case "car_transfer_request":
      title = "Ownership transfer";
      body = `The owner of ${carLabel} wants to transfer it to you.`;
      break;
    case "share_invite_response":
      title = "Invite response";
      body = `${notification.payload.accepted ? "Accepted" : "Declined"}: share for ${carLabel}.`;
      break;
    case "transfer_response":
      title = "Transfer response";
      body = `${notification.payload.accepted ? "Accepted" : "Declined"}: transfer of ${carLabel}.`;
      break;
    case "share_removed":
      title = "Share removed";
      body = `Your access to ${carLabel} was removed.`;
      break;
  }

  const isPending = status === "pending";

  return (
    <li className={"notif-row" + (isPending ? " notif-row--unread" : "")}>
      <div className="notif-row__body">
        <span className="notif-row__title">{title}</span>
        {body ? <span className="notif-row__sub">{body}</span> : null}
        {statusLabel ? (
          <span className="notif-row__meta">{statusLabel}</span>
        ) : null}
      </div>
      <div className="notif-row__actions">
        {actionable ? (
          <>
            <button
              type="button"
              className="button button--primary button--small"
              onClick={() => onRespond("accept")}
              disabled={busy}
            >
              Accept
            </button>
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => onRespond("decline")}
              disabled={busy}
            >
              Decline
            </button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

function ProfileSection() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();

  const [emailOpen, setEmailOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!me) return null;

  async function handleLogout() {
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <section className="page-section">
      <h2 className="section-title section-title--with-icon">
        <Icon name="user" className="section-title__icon" />
        <span>Profile</span>
      </h2>
      <div className="account-form">
        <div className="form-row">
          <span className="form-row__label">Username</span>
          <input
            type="text"
            value={me.username}
            readOnly
            className="form-row__input form-row__input--readonly"
          />
        </div>
        <div className="form-row">
          <span className="form-row__label">Email</span>
          <input
            type="email"
            value={me.email}
            readOnly
            className="form-row__input form-row__input--readonly"
          />
        </div>
        <div className="form-actions form-actions--inline">
          <button
            type="button"
            className="button button--ghost"
            onClick={() => void handleLogout()}
          >
            <Icon name="logout" />
            <span>Log out</span>
          </button>
          <MoreMenu
            render={({ close }) => (
              <>
                <MoreMenuItem
                  label="Change email"
                  onSelect={() => {
                    close();
                    setEmailOpen(true);
                  }}
                />
                <MoreMenuItem
                  label="Change password"
                  onSelect={() => {
                    close();
                    setPasswordOpen(true);
                  }}
                />
                <MoreMenuDivider />
                <MoreMenuItem
                  label="Delete my account"
                  destructive
                  onSelect={() => {
                    close();
                    setDeleteOpen(true);
                  }}
                />
              </>
            )}
          />
        </div>
      </div>

      <ChangeEmailModal
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        currentEmail={me.email}
      />
      <ChangePasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
      />
      <DeleteAccountModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Account actions
// ---------------------------------------------------------------------------

function AccountActionsSection() {
  return (
    <section className="page-section">
      <ul className="account-menu" aria-label="Account options">
        <li>
          <Link to="/garage" className="account-menu__row">
            <span className="account-menu__lead">
              <Icon name="garage" className="account-menu__icon" />
              <span className="account-menu__label">Garage</span>
            </span>
            <Icon name="chevron-right" className="account-menu__chevron" />
          </Link>
        </li>
        <li>
          <Link to="/fuel-stations" className="account-menu__row">
            <span className="account-menu__lead">
              <Icon name="pin" className="account-menu__icon" />
              <span className="account-menu__label">Fuel stations</span>
            </span>
            <Icon name="chevron-right" className="account-menu__chevron" />
          </Link>
        </li>
        <li>
          <Link to="/account/settings" className="account-menu__row">
            <span className="account-menu__lead">
              <Icon name="settings" className="account-menu__icon" />
              <span className="account-menu__label">Settings</span>
            </span>
            <Icon name="chevron-right" className="account-menu__chevron" />
          </Link>
        </li>
      </ul>
    </section>
  );
}
